import {
	type FormModel,
	type FormModelFieldState,
	type FormModelMetadata,
	FormModelState,
	type IMap,
} from '@ikegps/core-shared';
import { action, computed, makeObservable } from 'mobx';
import { ObservableIMap } from '../util/observable.map';

export class FormModelEditState extends FormModelState {
	internalState: FormModelState;
	override fields: IMap<FormModelFieldState> = new ObservableIMap<FormModelFieldState>();
	properties: IMap<string> = new ObservableIMap<string>();

	override get id(): string {
		return this.internalState.id;
	}
	override get revision(): string {
		return this.internalState.revision;
	}
	override get version(): string {
		return this.internalState.version;
	}
	override get rootFormId(): string {
		return this.internalState.rootFormId;
	}
	override get name(): string {
		return this.internalState.name;
	}
	override get displayVersion(): number {
		return this.internalState.displayVersion;
	}
	override get updatedAt(): Date {
		return this.internalState.updatedAt;
	}
	override get createdAt(): Date {
		return this.internalState.createdAt;
	}
	override get updatedBy(): string {
		return this.internalState.updatedBy;
	}
	override get v(): number {
		return this.internalState.v;
	}
	override get metadata(): FormModelMetadata {
		return this.internalState.metadata;
	}

	constructor(form: FormModel) {
		super(form);
		makeObservable(this);

		this.internalState = form.state;
		this.setDisplayName(form.displayName);
		this.setDescription(form.description);
	}

	@computed get dirty(): boolean {
		if (this.internalState.fields.size > 0 && this.fields.size > 0) {
			return true;
		}

		return (
			this.fields.size > 0 ||
			this.internalState.displayName !== this.displayName ||
			this.internalState.description !== this.description
		);
	}

	@computed get valid(): boolean {
		for (const field of Array.from(this.fields.values())) {
			if (field.displayName == null || field.displayName.trim().length === 0) {
				return false;
			}
		}

		return true;
	}

	@computed get isDirty(): boolean {
		if (this.internalState.fields.size > 0 && this.fields.size > 0) {
			return true;
		}

		return this.fields.size > 0 || this.properties.size > 0;
	}

	@action reset() {
		this.fields.clear();
		this.properties.clear();
	}

	override get displayName(): string {
		return this.getProperty('displayName');
	}

	override get description(): string {
		return this.getProperty('description');
	}

	override setDisplayName(displayName: string) {
		this.setProperty('displayName', displayName);
	}

	override setDescription(description: string) {
		this.setProperty('description', description);
	}

	private getProperty<T extends keyof FormModelState>(propertyName: T) {
		if (this.properties.has(propertyName)) {
			return this.properties.get(propertyName);
		}

		return this.internalState[propertyName];
	}

	@action private setProperty<T extends keyof FormModelState>(propertyName: T, value: string) {
		let internalValue = this.internalState[propertyName];

		if (JSON.stringify(value) === JSON.stringify(internalValue)) {
			this.properties.delete(propertyName);
		} else {
			this.properties.set(propertyName, value);
		}
	}

	override getFieldValue(fieldId: string): FormModelFieldState {
		if (this.fields.has(fieldId)) {
			return this.fields.get(fieldId);
		}

		return this.internalState.getFieldValue(fieldId);
	}

	override setFieldValue<T extends FormModelFieldState>(fieldId: string, state: T) {
		let internalValue = this.internalState.getFieldValue(fieldId);

		if (JSON.stringify(state) === JSON.stringify(internalValue)) {
			this.fields.delete(fieldId);
		} else {
			this.fields.set(fieldId, state);
		}
	}
}
